import React, { lazy } from 'react'
import { CardHeader, Grid, Divider } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Condition from 'components/Condition'
import JobShare from 'components/JobShare'
import Suspense from 'components/Suspense'
import Box from 'components/Box'
import MoreButtonCard from 'components/MoreButtonCard'
import { Share } from '@mui/icons-material'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { JobPostingContent_Job$key } from '__generated__/JobPostingContent_Job.graphql'
import { useChatAutomation } from 'hooks/chat'
import { useFormFieldFromContext } from 'hooks/form'
import { useResponsiveBreakpoint } from 'hooks/useResponsiveBreakpoint'
import { useMediaQuery } from 'hooks/useMediaQuery'
import JobTitleField from '../Fields/JobTitleField'
import JobRatesFields from '../Fields/JobRatesFields'
import JobTypeFields from '../Fields/JobTypeFields'
import JobLocationFields from '../Fields/JobLocationFields'
import JobDescriptionField from '../Fields/JobDescriptionField'
import JobSkillsFields from '../Fields/JobSkillsFields'
import ProjectLengthFields from '../Fields/ProjectLengthFields'
import useStyles from './styles'
import JobSpecializationsFields from '../Fields/JobSpecializationsFields'
import CompanyNameFields from '../Fields/CompanyNameFields'
import CompanyDescriptionField from '../Fields/CompanyDescriptionField'
import CompanyWebsiteField from '../Fields/CompanyWebsiteField'
import JobPostingManagerField from '../Fields/JobPostingManagerField'
import { IJobFormFields } from '../JobFormWrapper/JobFormWrapper'
import OfficePolicyInfo from '../OfficePolicyInfo/OfficePolicyInfo'

// Mapbox does not work on the server side
const JobLocationMap = lazy(() => (
  import(/* webpackChunkName: "JobLocationMap" */'../JobLocationMap')
))

interface IJobPostingContentProps {
  jobFragmentRef?: JobPostingContent_Job$key
  editable?: boolean
  noCompany?: boolean
  hideTutorialBubble?: boolean
}

const JobPostingContent = (props: IJobPostingContentProps) => {
  const { classes } = useStyles()
  const theme = useTheme()
  const {
    editable,
    jobFragmentRef,
    hideTutorialBubble,
    noCompany,
  } = props

  const job = useFragment(graphql`
    fragment JobPostingContent_Job on Job {
      id
      locationLatitude
      locationLongitude
      incognito
      enableAutomatedMessaging
      jobStatus: status

      firm {
        self
      }
     
      ...JobRatesFields_Job
      ...OfficePolicyInfo_Job
      ...JobShare_Job
    }
  `, jobFragmentRef)

  const title = useFormFieldFromContext<IJobFormFields>('title', 'typing')

  const positionTypes = useFormFieldFromContext<IJobFormFields>('position_types', 'selection')
  const clientRate = useFormFieldFromContext<IJobFormFields>('client_rate', 'typing')
  const minClientRate = useFormFieldFromContext<IJobFormFields>('min_client_rate', 'typing')
  const freelancerRate = useFormFieldFromContext<IJobFormFields>('freelancer_rate', 'typing')
  const minFreelancerRate = useFormFieldFromContext<IJobFormFields>('min_freelancer_rate', 'typing')
  const rateMode = useFormFieldFromContext<IJobFormFields>('rate_mode', 'selection')
  const currency = useFormFieldFromContext<IJobFormFields>('currency', 'selection')

  const industryId = useFormFieldFromContext<IJobFormFields>('industry_id', 'selection')
  const jobSkills = useFormFieldFromContext<IJobFormFields>('job_skills', 'selection')

  const specializations = useFormFieldFromContext<IJobFormFields>('specializations', 'selection')
  const requiredExperienceYears = useFormFieldFromContext<IJobFormFields>('required_experience_years', 'selection')

  const locationType = useFormFieldFromContext<IJobFormFields>('location_type', 'selection')
  const jobCountries = useFormFieldFromContext<IJobFormFields>('job_countries', 'selection')
  const jobTimezone = useFormFieldFromContext<IJobFormFields>('job_timezone', 'selection')
  const timezoneValue = useFormFieldFromContext<IJobFormFields>('timezone_value', 'selection')
  const timezoneRange = useFormFieldFromContext<IJobFormFields>('timezone_range', 'selection')
  const fullAddress = useFormFieldFromContext<IJobFormFields>('full_address', 'typing')
  const country = useFormFieldFromContext<IJobFormFields>('country', 'selection')
  const region = useFormFieldFromContext<IJobFormFields>('region', 'typing')
  const city = useFormFieldFromContext<IJobFormFields>('city', 'typing')
  const locationLatitude = useFormFieldFromContext<IJobFormFields>('location_latitude', 'selection')
  const locationLongitude = useFormFieldFromContext<IJobFormFields>('location_longitude', 'selection')
  const defaultDistance = useFormFieldFromContext<IJobFormFields>('default_distance', 'typing')

  const projectLengthInMonths = useFormFieldFromContext<IJobFormFields>('project_length_in_months', 'selection')

  const companyName = useFormFieldFromContext<IJobFormFields>('company_name', 'typing')
  const companyWebsite = useFormFieldFromContext<IJobFormFields>('company_website', 'typing')
  const companyDescription = useFormFieldFromContext<IJobFormFields>('company_description', 'typing')

  const description = useFormFieldFromContext<IJobFormFields>('description', 'typing')
  const descriptionResponsibilities = useFormFieldFromContext<IJobFormFields>('description_responsibilities', 'typing')
  const descriptionExperience = useFormFieldFromContext<IJobFormFields>('description_experience', 'typing')

  const userId = useFormFieldFromContext<IJobFormFields>('user_id', 'selection')

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useResponsiveBreakpoint(theme.breakpoints.values.md)
  const isDesktop = useResponsiveBreakpoint(theme.breakpoints.values.lg)

  const enableChatAutomation = Boolean(job?.jobStatus === 'opened' && !job.firm?.self && !job.incognito && job.enableAutomatedMessaging)
  useChatAutomation('JobContentPeekMessage', { jobId: job.id, enabled: enableChatAutomation })

  return (
    <div className={classes.mainAreaWrapper}>
      <Box variant={isSmallScreen ? 'default' : 'wide'}>
        <Grid container spacing={isSmallScreen ? 2 : 4}>
          <Grid item xs={12}>
            <CardHeader
              className={classes.subheader}
              title={(
                <JobTitleField
                  title={title}
                  editable={editable}
                  hideTutorialBubble={hideTutorialBubble}
                  status={null}
                />
              )}
              subheader={(
                <JobRatesFields
                  position_types={positionTypes}
                  client_rate={clientRate}
                  min_client_rate={minClientRate}
                  freelancer_rate={freelancerRate}
                  min_freelancer_rate={minFreelancerRate}
                  rate_mode={rateMode}
                  currency={currency}
                  editable={editable}
                  job={job}
                />
              )}
              action={!editable && (
                <React.Fragment>
                  <Condition condition={!isTablet}>
                    <JobShare jobFragmentRef={job} />
                  </Condition>

                  <Condition condition={isTablet}>
                    <MoreButtonCard icon={<Share />}>
                      <JobShare jobFragmentRef={job} />
                    </MoreButtonCard>
                  </Condition>
                </React.Fragment>
              )}
              data-cy="job-card-header"
            />
          </Grid>

          <Grid item xs={12}>
            <div data-cy="job-info-tiles">
              <Grid container>
                <Suspense>
                  <JobTypeFields
                    industryId={industryId}
                    jobSkills={jobSkills}
                    specializations={specializations}
                    requiredExperienceYears={requiredExperienceYears}
                    editable={editable}
                  />
                </Suspense>

                <OfficePolicyInfo jobFragmentRef={job} />

                <JobLocationFields
                  location_type={locationType}
                  job_countries={jobCountries}
                  job_timezone={jobTimezone}
                  timezone_value={timezoneValue}
                  timezone_range={timezoneRange}
                  full_address={fullAddress}
                  country={country}
                  region={region}
                  city={city}
                  location_latitude={locationLatitude}
                  location_longitude={locationLongitude}
                  default_distance={defaultDistance}
                  editable={editable}
                />

                <ProjectLengthFields
                  project_length_in_months={projectLengthInMonths}
                  position_types={positionTypes}
                  editable={editable}
                />
              </Grid>
            </div>
          </Grid>

          <Grid item xs={12}>
            <div data-cy="job-skills">
              <Suspense>
                <JobSpecializationsFields
                  industryId={industryId}
                  specializations={specializations}
                  editable={editable}
                  label="Specializations"
                />
              </Suspense>

              <Suspense>
                <JobSkillsFields
                  job_skills={jobSkills}
                  industryId={industryId}
                  editable={editable}
                  requiredSkills
                  showError={false} // The JobSkillsFields is re-used twice, only the second one will display errors.
                  label="Required skills"
                />
              </Suspense>

              <Suspense>
                <JobSkillsFields
                  job_skills={jobSkills}
                  industryId={industryId}
                  editable={editable}
                  label="Nice-to-have skills"
                />
              </Suspense>
            </div>
          </Grid>
        </Grid>
      </Box>

      <Divider style={{ margin: 0 }} />

      <Box data-cy="job-text-description" variant={isSmallScreen ? 'default' : 'wide'}>
        <Grid container spacing={0}>
          <Condition condition={!noCompany}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <div data-cy="job-company-description">
                    <div>
                      <CompanyNameFields
                        companyName={companyName}
                        companyWebsite={companyWebsite}
                        editable={editable}
                      />
                    </div>

                    <div>
                      <CompanyWebsiteField
                        companyWebsite={companyWebsite}
                        editable={editable}
                      />
                    </div>
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <CompanyDescriptionField
                    companyDescription={companyDescription}
                    editable={editable}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Condition>

          <Grid item xs={12}>
            <JobDescriptionField
              formValue={description}
              editable={editable}
              label="About the role"
              defaultValue="Describe the role in a few lines"
            />
          </Grid>

          <Grid item xs={12}>
            <JobDescriptionField
              formValue={descriptionResponsibilities}
              editable={editable}
              label="Key responsibilities"
              defaultValue="What are the key responsibilities of the role"
            />
          </Grid>

          <Grid item xs={12}>
            <JobDescriptionField
              formValue={descriptionExperience}
              editable={editable}
              label="Ideal experience"
              defaultValue="What is the ideal experience for a candidate in this role"
            />
          </Grid>
        </Grid>
      </Box>

      {editable && (
        <div className={classes.internals}>
          <Grid container spacing={1}>
            <Suspense>
              <Grid item xs={12} md={6}>
                {/* TODO: this needs to be changed to the new component for managing associated users */}
                <JobPostingManagerField
                  input={userId.input}
                  meta={userId.meta}
                  editable={editable}
                />
              </Grid>
            </Suspense>
          </Grid>
        </div>
      )}

      {job?.locationLatitude && job?.locationLongitude && (
        <Suspense noSsr>
          <Condition condition={!isDesktop}>
            <div style={{ margin: theme.spacing(0, 0, 8, 0), borderTop: '1px solid rgba(0, 0, 0, 0.1)', borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
              <JobLocationMap latitude={job.locationLatitude} longitude={job.locationLongitude} />
            </div>
          </Condition>
        </Suspense>
      )}
      {(!job?.locationLatitude || !job?.locationLongitude) && <Divider />}
    </div>
  )
}

export default JobPostingContent
